/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ComponentType } from 'react';
import { create } from 'zustand';

export const RESPONSIVE_DIALOG_HISTORY_KEY = '__welloResponsiveDialog';

export interface ResponsiveDialogProps {
  [key: string]: any;
}

export interface ResponsiveDialogConfig {
  component: ComponentType<any>;
  props?: ResponsiveDialogProps;
  /** bottom sheet 헤더에 표시될 제목 */
  title?: string;
  /** 모달의 header 부분 엘리먼트 */
  modalHeader?:
    | React.ReactNode // 커스텀
    | string // title
    | { title: string; description?: string }; // title, description
  className?: {
    backdrop?: string;
    drawer?: string;
    /** 모달의 className max-w-* 설정할 때 사용 */
    modal?: string;
  };
  duration?: number;
  unmountOnExit?: boolean;
  mountOnEnter?: boolean;
  /** drawer padding 없이 표시 */
  drawerNoPadding?: boolean;
  /** full drawer 기본 헤더 숨기기 (컴포넌트에서 직접 헤더 구현 시) */
  hideDrawerHeader?: boolean;
  drawerType?: 'bottom' | 'full';
  /** 좁은 화면에서도 drawer 대신 모달로 표시 */
  forceModal?: boolean;
}

interface ResponsiveDialogStore {
  /** 현재 열린 Dialog 설정 */
  currentDialog: ResponsiveDialogConfig | null;
  isOpen: boolean;
  openDialog: (config: ResponsiveDialogConfig) => void;
  closeDialog: () => void;
  /** Dialog 설정 업데이트 함수 */
  updateDialogProps: (props: ResponsiveDialogProps) => void;
  /** Dialog config 업데이트 함수 */
  updateDialogConfig: (config: Partial<ResponsiveDialogConfig>) => void;
}

export const useResponsiveDialogStore = create<ResponsiveDialogStore>(
  (set, get) => ({
    currentDialog: null,
    isOpen: false,

    openDialog: (config: ResponsiveDialogConfig) => {
      set({
        currentDialog: config,
        isOpen: true,
      });
    },

    closeDialog: () => {
      // ResponsiveDialog 공통 상태만 닫는다.
      // 개별 dialog content가 넘긴 onClose 후처리는 호출하지 않으므로,
      // 사용자/브라우저/프로그램 닫기 요청은 아래 requestCloseResponsiveDialog를 통해 처리한다.
      set({
        isOpen: false,
      });

      // 애니메이션 완료 후 drawer 설정 초기화
      setTimeout(() => {
        const { isOpen } = get();
        if (!isOpen) {
          set({ currentDialog: null });
        }
      }, get().currentDialog?.duration || 300);
    },

    updateDialogProps: (props: ResponsiveDialogProps) => {
      const { currentDialog } = get();
      if (currentDialog) {
        set({
          currentDialog: {
            ...currentDialog,
            props: {
              ...currentDialog.props,
              ...props,
            },
          },
        });
      }
    },

    updateDialogConfig: (config: Partial<ResponsiveDialogConfig>) => {
      const { currentDialog } = get();
      if (currentDialog) {
        set({
          currentDialog: {
            ...currentDialog,
            ...config,
            // props가 config에 포함된 경우 기존 props와 병합
            props: config.props
              ? {
                  ...currentDialog.props,
                  ...config.props,
                }
              : currentDialog.props,
          },
        });
      }
    },
  }),
);

// 편의 함수들

export const closeResponsiveDialogWithoutHistory = () => {
  const { currentDialog, closeDialog } = useResponsiveDialogStore.getState();

  // popstate처럼 browser history가 이미 이동한 뒤에는 다시 history.back()을 호출하면
  // 실제 이전 페이지로 한 번 더 이동할 수 있다. 이 함수는 history를 건드리지 않고
  // content onClose 후처리와 ResponsiveDialog 상태 닫기만 수행한다.
  currentDialog?.props?.onClose?.();
  closeDialog();
};

export const requestCloseResponsiveDialog = () => {
  const { currentDialog, isOpen } = useResponsiveDialogStore.getState();

  if (!isOpen || !currentDialog) return false;

  // ResponsiveDialog가 열릴 때 push한 history entry가 현재 위치라면,
  // 직접 닫지 않고 history.back()으로 해당 entry를 소비한다.
  // 실제 상태 닫기는 ResponsiveDialogContainer의 popstate listener에서 처리한다.
  if (
    typeof window !== 'undefined' &&
    window.history.state?.[RESPONSIVE_DIALOG_HISTORY_KEY]
  ) {
    window.history.back();

    return true;
  }

  closeResponsiveDialogWithoutHistory();

  return true;
};

/**
 * ResponsiveDialog를 열기 위한 함수
 */
export const openResponsiveDialog = <T extends ComponentType<any>>(
  config: Omit<ResponsiveDialogConfig, 'component' | 'props'> & {
    component: T;
    props?: React.ComponentProps<T>;
  },
) => {
  useResponsiveDialogStore.getState().openDialog(config);
};

export const closeResponsiveDialog = () => {
  requestCloseResponsiveDialog();
};

export const updateResponsiveDialogConfig = (
  config: Partial<ResponsiveDialogConfig>,
) => {
  useResponsiveDialogStore.getState().updateDialogConfig(config);
};
